import { baseApi } from './baseApi';
import { normalizeKeys } from '../../utils/transform';

export interface BillingProfile {
  orgId?: string;
  businessName: string;
  billingEmail: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  taxId: string;
}

export interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
}

export interface Invoice {
  id: string;
  date: string;
  invoiceNumber: string;
  amount: number;
  status: 'paid' | 'pending' | 'failed';
  downloadUrl?: string;
  periodStart?: string;
  periodEnd?: string;
  subscriptionUsd?: number;
  usageUsd?: number;
  currency?: string;
}

export interface PaymentReadiness {
  orgId?: string;
  hasPaymentMethod: boolean;
  cardRequired: boolean;
  reason?: string | null;
  estimatedTotalUsd?: number;
  inFreePeriod?: boolean;
  freeUntil?: string | null;
  stripeConfigured?: boolean;
}

export interface SetupIntentResponse {
  clientSecret: string;
  setupIntentId: string;
  customerId: string;
  orgId?: string;
}

export interface StripeConfig {
  publishableKey: string;
  configured: boolean;
  orgId?: string;
}

export interface OrgBudgetConfig {
  tokenCap?: number;
  period?: string;
  alertThresholdPct?: number;
}

export interface BillingBudgetResponse {
  organisation: {
    orgId: string;
    budgetConfig?: OrgBudgetConfig | null;
  };
  applications: Array<{
    applicationId: string;
    name: string;
    budgetConfig?: OrgBudgetConfig | null;
  }>;
}

/** Period bill from BillCalculator (portal, staff=False). */
export interface PeriodBillResponse {
  from: string;
  to: string;
  currency: string;
  tokensTotal: number;
  preDiscountCostUsd: number;
  includedThresholdUsd: number;
  subscriptionFeeUsd: number;
  subscriptionPaidUsd: number;
  usageChargesUsd: number;
  totalUsd: number;
  overageUsd: number;
  remainingIncludedUsd: number;
  tier: 'included' | 'usage';
  joinedAt?: string | null;
  inFreePeriod?: boolean;
  freeUntil?: string | null;
  freeSubscriptionMonths?: number;
}

export interface UsageCostBreakdownRow {
  key: string;
  label: string;
  usageCostUsd: number;
  sharePct: number;
  tokensTotal?: number;
  applicationId?: string | null;
  applicationName?: string | null;
}

export interface UsageCostBreakdownResponse {
  dimension: string;
  from: string;
  to: string;
  currency: string;
  totalUsageCostUsd: number;
  rows: UsageCostBreakdownRow[];
}

export interface UsageMomSeriesRow {
  month: string;
  from: string;
  to: string;
  subscriptionPaidUsd: number;
  usageChargesUsd: number;
  totalUsd: number;
  preDiscountCostUsd: number;
  inFreePeriod?: boolean;
}

export interface UsageMomResponse {
  months: number;
  currency: string;
  kpis: {
    thisMonthUsd: number;
    trailingAverageUsd: number;
    highestMonth: string | null;
    highestMonthUsd: number;
  };
  series: UsageMomSeriesRow[];
}

function profileToApiBody(body: Partial<BillingProfile>) {
  return {
    business_name: body.businessName,
    billing_email: body.billingEmail,
    address_line1: body.addressLine1,
    address_line2: body.addressLine2,
    city: body.city,
    state: body.state,
    postal_code: body.postalCode,
    country: body.country,
    tax_id: body.taxId,
  };
}

export const billingApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getBillingProfile: builder.query<BillingProfile, void>({
      query: () => '/portal/billing/profile',
      transformResponse: (response: unknown) => normalizeKeys<BillingProfile>(response),
      providesTags: ['Billing'],
    }),

    updateBillingProfile: builder.mutation<BillingProfile, Partial<BillingProfile>>({
      query: (body) => ({
        url: '/portal/billing/profile',
        method: 'PUT',
        body: profileToApiBody(body),
      }),
      invalidatesTags: ['Billing'],
    }),

    getPaymentMethods: builder.query<{ paymentMethods: PaymentMethod[]; orgId?: string }, void>({
      query: () => '/portal/billing/payment-methods',
      transformResponse: (response: unknown) =>
        normalizeKeys<{ paymentMethods: PaymentMethod[]; orgId?: string }>(response),
      providesTags: ['Billing'],
    }),

    createSetupIntent: builder.mutation<SetupIntentResponse, void>({
      query: () => ({
        url: '/portal/billing/setup-intent',
        method: 'POST',
        body: {},
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<SetupIntentResponse>(response),
    }),

    getStripeConfig: builder.query<StripeConfig, void>({
      query: () => '/portal/billing/stripe-config',
      transformResponse: (response: unknown) =>
        normalizeKeys<StripeConfig>(response),
      providesTags: ['Billing'],
    }),

    setDefaultPaymentMethod: builder.mutation<
      { paymentMethods: PaymentMethod[] },
      { paymentMethodId: string }
    >({
      query: (body) => ({
        url: '/portal/billing/payment-methods/default',
        method: 'POST',
        body: { payment_method_id: body.paymentMethodId },
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<{ paymentMethods: PaymentMethod[] }>(response),
      invalidatesTags: ['Billing'],
    }),

    detachPaymentMethod: builder.mutation<
      { paymentMethods: PaymentMethod[] },
      { paymentMethodId: string }
    >({
      query: ({ paymentMethodId }) => ({
        url: `/portal/billing/payment-methods/${paymentMethodId}`,
        method: 'DELETE',
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<{ paymentMethods: PaymentMethod[] }>(response),
      invalidatesTags: ['Billing'],
    }),

    getPaymentReadiness: builder.query<PaymentReadiness, void>({
      query: () => '/portal/billing/payment-readiness',
      transformResponse: (response: unknown) =>
        normalizeKeys<PaymentReadiness>(response),
      providesTags: ['Billing'],
    }),

    getInvoices: builder.query<{ invoices: Invoice[] }, void>({
      query: () => '/portal/billing/invoices',
      transformResponse: (response: unknown) =>
        normalizeKeys<{ invoices: Invoice[] }>(response),
      providesTags: ['Invoice'],
    }),

    retryInvoice: builder.mutation<Invoice, { invoiceId: string }>({
      query: ({ invoiceId }) => ({
        url: `/portal/billing/invoices/${invoiceId}/retry`,
        method: 'POST',
        body: {},
      }),
      transformResponse: (response: unknown) => normalizeKeys<Invoice>(response),
      invalidatesTags: ['Invoice', 'Billing'],
    }),

    getBillingBudget: builder.query<BillingBudgetResponse, void>({
      query: () => '/portal/billing/budget',
      transformResponse: (response: unknown) =>
        normalizeKeys<BillingBudgetResponse>(response),
      providesTags: ['Billing'],
    }),

    updateBillingBudget: builder.mutation<
      BillingBudgetResponse,
      { budgetConfig: OrgBudgetConfig }
    >({
      query: (body) => ({
        url: '/portal/billing/budget',
        method: 'PATCH',
        body: {
          budget_config: {
            token_cap: body.budgetConfig.tokenCap,
            period: body.budgetConfig.period || 'monthly',
            alert_threshold_pct: body.budgetConfig.alertThresholdPct,
          },
        },
      }),
      invalidatesTags: ['Billing', 'Analytics'],
    }),

    getUsageCharges: builder.query<
      PeriodBillResponse,
      { from?: string; to?: string } | void
    >({
      query: (args) => {
        const params = new URLSearchParams();
        if (args && 'from' in args && args.from) params.set('from', args.from);
        if (args && 'to' in args && args.to) params.set('to', args.to);
        const qs = params.toString();
        return `/portal/billing/usage-charges${qs ? `?${qs}` : ''}`;
      },
      transformResponse: (response: unknown) =>
        normalizeKeys<PeriodBillResponse>(response),
      providesTags: ['Billing'],
    }),

    getUsageCostBreakdown: builder.query<
      UsageCostBreakdownResponse,
      { dimension?: string; from?: string; to?: string } | void
    >({
      query: (args) => {
        const params = new URLSearchParams();
        if (args && 'dimension' in args && args.dimension)
          params.set('dimension', args.dimension);
        if (args && 'from' in args && args.from) params.set('from', args.from);
        if (args && 'to' in args && args.to) params.set('to', args.to);
        const qs = params.toString();
        return `/portal/billing/usage-cost-breakdown${qs ? `?${qs}` : ''}`;
      },
      transformResponse: (response: unknown) =>
        normalizeKeys<UsageCostBreakdownResponse>(response),
      providesTags: ['Billing'],
    }),

    getUsageMom: builder.query<UsageMomResponse, { months?: number } | void>({
      query: (args) => {
        const params = new URLSearchParams();
        if (args && 'months' in args && args.months != null)
          params.set('months', String(args.months));
        const qs = params.toString();
        return `/portal/billing/usage-mom${qs ? `?${qs}` : ''}`;
      },
      transformResponse: (response: unknown) =>
        normalizeKeys<UsageMomResponse>(response),
      providesTags: ['Billing'],
    }),
  }),
});

export const {
  useGetBillingProfileQuery,
  useUpdateBillingProfileMutation,
  useGetPaymentMethodsQuery,
  useCreateSetupIntentMutation,
  useGetStripeConfigQuery,
  useSetDefaultPaymentMethodMutation,
  useDetachPaymentMethodMutation,
  useGetPaymentReadinessQuery,
  useGetInvoicesQuery,
  useRetryInvoiceMutation,
  useGetBillingBudgetQuery,
  useUpdateBillingBudgetMutation,
  useGetUsageChargesQuery,
  useGetUsageCostBreakdownQuery,
  useGetUsageMomQuery,
} = billingApi;
